import * as React from "react";

import { EditOutlined } from "@ant-design/icons";
import { FormItem, FormLabel } from "@impero-com/ui";

import { CustomizedValidation, ValidationRule } from "utils/form";

import * as theme from "./theme.css";

/**
 The Base FormField Props
 * @field label the label, "title" of this field
 * @field getComponent is a callback creating a React.ReactNode containing the definition of the inner input field
 * @field onClick is called when a click is performed on the label. Useful to give focus to the field
 * @field hasFeedback uses antd's hasFeedback functionality
 * @field readonly display the readonly component. Throw an error if true and getReadonlyComponent is undefined
 * @field getReadonlyComponent is a callback creating a React.ReactNode containing the definition of the readonly field
 */
interface BaseFormFieldProps {
    label?: string;
    getComponent: () => React.ReactNode;
    onClick?: React.MouseEventHandler;
    hasFeedback: boolean;
    readonly?: boolean;
    getReadonlyComponent?: () => React.ReactNode;
    validationError?: CustomizedValidation;
}

// BaseFormField can't be PureComponent because components from getComponent
// needs to be re-render when form field is updated.
class BaseFormField extends React.Component<BaseFormFieldProps> {
    render = () => {
        const {
            getComponent,
            getReadonlyComponent,
            hasFeedback,
            label,
            onClick,
            readonly,
            validationError,
        } = this.props;

        if (readonly && !getReadonlyComponent) {
            throw new Error("getReadonlyComponent is undefined");
        }

        const hasEditMode = !!getReadonlyComponent;
        const component = getComponent();

        const toDisplay = readonly ? (
            <div className={theme.readOnlyContent}>
                <span onClick={onClick}>{getReadonlyComponent!()}</span>
            </div>
        ) : (
            component
        );

        return (
            <FormItem
                hasFeedback={hasFeedback}
                validationError={validationError}
            >
                {(label || hasEditMode) && (
                    <FormLabel showPencil={hasEditMode} onClick={onClick}>
                        {label}
                    </FormLabel>
                )}
                {toDisplay}
            </FormItem>
        );
    };
}

// LiteFormField can't be PureComponent because components from getComponent
// needs to be re-render when form field is updated.
/**
 * Smaller version of a form component, without label
 */
class LiteFormField extends React.Component<Omit<BaseFormFieldProps, "label">> {
    render = () => {
        const {
            getComponent,
            getReadonlyComponent,
            hasFeedback,
            onClick,
            readonly,
        } = this.props;

        if (readonly && !getReadonlyComponent) {
            throw new Error("getReadonlyComponent is undefined");
        }

        const hasEditMode = !!getReadonlyComponent;
        const component = getComponent();

        const toDisplay = readonly ? (
            <div className={theme.readOnlyContent}>
                <span onClick={onClick}>
                    {getReadonlyComponent!()}
                    {hasEditMode && (
                        <EditOutlined
                            className={theme.imperoFormFieldIcon}
                            onClick={onClick}
                        />
                    )}
                </span>
            </div>
        ) : (
            component
        );

        return (
            <FormItem lite hasFeedback={hasFeedback}>
                {toDisplay}
            </FormItem>
        );
    };
}

const EditInput = <
    T,
    Props extends EditProps<T | undefined> = EditProps<T | undefined>,
>(
    EditInputForm: React.ComponentClass<Props & EditFormProps<T>>
) =>
    class extends React.PureComponent<Props, EditState<T>> {
        mounted = false;
        state: EditState<T> = {
            pendingChanges: false,
            changeErrors: undefined,
            lock: UNLOCKED,
        };

        render = () => {
            return (
                <EditInputForm
                    {...this.props}
                    {...this.state}
                    setPendingChanges={this.setPendingChanges}
                    setChangeErrors={this.setChangeErrors}
                    lockValue={this.lockValue}
                    unlockValue={this.unlockValue}
                />
            );
        };

        setPendingChanges = (pendingChanges: boolean) => {
            if (!this.mounted) {
                return;
            }
            this.setState({
                pendingChanges,
                changeErrors: undefined,
                lock: pendingChanges ? this.state.lock : UNLOCKED,
            });
        };

        setChangeErrors = (changeErrors: ChangeError[]) => {
            if (!this.mounted) {
                return;
            }
            this.setState({
                pendingChanges: false,
                changeErrors,
            });
        };

        lockValue = (
            value: T,
            validating: boolean | undefined,
            error: ChangeError[] | undefined
        ) => {
            this.setState({
                lock: { type: "Locked", value },
                pendingChanges: validating ?? false,
                changeErrors: error,
            });
        };

        unlockValue = () => {
            if (this.state.lock.type === "Locked") {
                this.setState({
                    lock: UNLOCKED,
                    pendingChanges: false,
                    changeErrors: undefined,
                });
            }
        };

        componentDidMount = () => {
            this.mounted = true;
        };

        componentWillUnmount = () => {
            this.mounted = false;
        };
    };

interface EditProps<T, V = T> {
    id: string;
    validators?: ValidationRule<V>[];
    value: T;
    onChange: (value: T) => Promise<unknown> | void;
    validationError?: CustomizedValidation;
}

interface EditState<T> {
    pendingChanges: boolean;
    changeErrors: ChangeError[] | undefined;
    // This is an enum because T=undefined should be supported
    lock: ValueLock<T>;
}

interface ChangeError {
    message: string;
    field: string;
}

interface EditFormProps<T> extends EditState<T> {
    setPendingChanges: (pendingChanges: boolean) => void;
    setChangeErrors: (changeErrors: ChangeError[]) => void;
    lockValue: (
        value: T,
        pendingChanges: boolean | undefined,
        changeErrors: ChangeError[] | undefined
    ) => void;
    unlockValue: () => void;
}

type ValueLock<T> = { type: "Locked"; value: T } | { type: "Unlocked" };

// Export unlocked state in order the prevent unwanted re-render
const UNLOCKED = Object.freeze({ type: "Unlocked" } as const);
export type {
    BaseFormFieldProps,
    ChangeError,
    EditFormProps,
    EditProps,
    EditState,
    ValueLock,
};
export { BaseFormField, LiteFormField, EditInput, UNLOCKED };
